<template>
  <v-dialog v-model="internalOpen" max-width="520" persistent>
    <div class="ai-visibility-delete-report-dialog">
      <div class="ai-visibility-delete-report-dialog__head">
        <h2>Delete this report?</h2>
        <button
            type="button"
            class="ai-visibility-delete-report-dialog__close"
            aria-label="Close delete confirmation"
            :disabled="deleting"
            @click="close"
        >
          <i class="material-icons">close</i>
        </button>
      </div>

      <div class="ai-visibility-delete-report-dialog__body">
        <div class="ai-visibility-delete-report-dialog__warning">
          <span class="mdi mdi-trash-can-outline"></span>
        </div>

        <p class="ai-visibility-delete-report-dialog__subtitle">
          You're about to delete <strong>"{{ trackerLabel }}"</strong>
          (Report ID: {{ trackerId }}). This can't be undone.
        </p>

        <ul class="ai-visibility-delete-report-dialog__loss-list">
          <li>
            <i class="material-icons-outlined">check</i>
            {{ questionCountLabel }}
          </li>
          <li>
            <i class="material-icons-outlined">insights</i>
            All weekly visibility history &amp; charts
          </li>
          <li>
            <i class="material-icons-outlined">article</i>
            Citation &amp; competitor records for this report
          </li>
        </ul>

        <p class="ai-visibility-delete-report-dialog__note">
          Credits already spent on past refreshes are not refunded. Other
          reports in this project aren't affected.
        </p>

        <label class="ai-visibility-delete-report-dialog__confirm">
          <span>Type <strong>DELETE</strong> to confirm</span>
          <input
              ref="confirmInput"
              v-model="confirmationText"
              type="text"
              placeholder="DELETE"
              autocomplete="off"
              :disabled="deleting"
              @keydown.enter.prevent="confirm"
          >
        </label>
      </div>

      <div class="ai-visibility-delete-report-dialog__actions">
        <button
            type="button"
            class="ai-visibility-delete-report-dialog__cancel"
            :disabled="deleting"
            @click="close"
        >
          Cancel
        </button>
        <button
            type="button"
            class="ai-visibility-delete-report-dialog__delete"
            :disabled="!canConfirm || deleting"
            @click="confirm"
        >
          {{ deleting ? 'Deleting...' : 'Delete report' }}
        </button>
      </div>
    </div>
  </v-dialog>
</template>

<script>
export default {
  name: 'DeleteTrackerDialog',
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    tracker: {
      type: Object,
      default: null,
    },
    trackerLabel: {
      type: String,
      default: 'this report',
    },
    deleting: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      confirmationText: '',
    }
  },
  computed: {
    internalOpen: {
      get() {
        return this.open
      },
      set(value) {
        if (!value) {
          this.close()
        }
      },
    },
    canConfirm() {
      return this.confirmationText.trim() === 'DELETE'
    },
    trackerId() {
      return this.tracker?.id || ''
    },
    questionCountLabel() {
      const count = Number(this.tracker?.questionCount || 0)
      return `${count} tracked ${count === 1 ? 'question' : 'questions'}`
    },
  },
  watch: {
    open(value) {
      if (value) {
        this.confirmationText = ''
        this.$nextTick(() => {
          const input = this.$refs.confirmInput
          if (input) {
            input.focus()
          }
        })
      }
    },
  },
  methods: {
    close() {
      if (this.deleting) {
        return
      }
      this.$emit('close')
    },
    confirm() {
      if (!this.canConfirm || this.deleting) {
        return
      }
      this.$emit('confirm', this.tracker)
    },
  },
}
</script>

<style scoped lang="scss">

.ai-visibility-delete-report-dialog__subtitle {
  strong {
    color: var(--v-mainColor-base);
  }
}

.ai-visibility-delete-report-dialog {
  border-radius: 8px;
  background: var(--v-cardBg-base);
  color: var(--v-mainColor-base);
  overflow: hidden;
}

.ai-visibility-delete-report-dialog__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 22px 24px;
}

.ai-visibility-delete-report-dialog__close {
  display: inline-flex;
  width: 30px;
  height: 30px;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  color: var(--v-mainColor-base);
  cursor: pointer;
  padding: 0;
}

.ai-visibility-delete-report-dialog__close i {
  font-size: 18px;
}

.ai-visibility-delete-report-dialog h2 {
  color: var(--v-mainColor-base);
  font-size: 22px;
  font-weight: 800;
  line-height: 1.25;
  margin: 0;
}

.ai-visibility-delete-report-dialog__body {
  padding: 24px;
}

.ai-visibility-delete-report-dialog p {
  color: var(--v-darkGrayColor-base);
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 18px;
}

.ai-visibility-delete-report-dialog__warning {
  display: inline-flex;
  width: 46px;
  height: 46px;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: rgba(255, 31, 45, 0.1);
  color: var(--v-redColor-base);
  margin-bottom: 16px;
}

.ai-visibility-delete-report-dialog__warning span {
  font-size: 24px;
}

.ai-visibility-delete-report-dialog__loss-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  list-style: none;
  margin: 0 0 18px;
  background-color: var(--v-gray7Color-base);
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid var(--v-gray15Color-base);
}

.ai-visibility-delete-report-dialog__loss-list li {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--v-mainColor-base);
  font-size: 14px;
  line-height: 1.35;
}

.ai-visibility-delete-report-dialog__loss-list i {
  color: var(--v-mainColor-base);
  font-size: 18px;
}

.ai-visibility-delete-report-dialog__note {
  font-size: 13px;
}

.ai-visibility-delete-report-dialog__confirm {
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: var(--v-mainColor-base);
  font-size: 13px;
  font-weight: 600;
}

.ai-visibility-delete-report-dialog__confirm input {
  min-height: 40px;
  border: 1px solid var(--v-gray13Color-base);
  border-radius: 8px;
  color: var(--v-mainColor-base);
  font-size: 14px;
  padding: 8px 10px;
}

.ai-visibility-delete-report-dialog__confirm input:focus {
  border-color: var(--v-mainColor-base);
  outline: none;
}

.ai-visibility-delete-report-dialog__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  border-top: 1px solid var(--v-gray13Color-base);
  padding: 18px 24px;
}

.ai-visibility-delete-report-dialog__cancel,
.ai-visibility-delete-report-dialog__delete {
  min-height: 38px;
  border-radius: 999px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  padding: 0 18px;
}

.ai-visibility-delete-report-dialog__cancel {
  border: 1px solid var(--v-gray13Color-base);
  background: transparent;
  color: var(--v-mainColor-base);
}

.ai-visibility-delete-report-dialog__delete {
  border: 1px solid var(--v-redColor-base);
  background: var(--v-redColor-base);
  color: var(--v-whiteColor-base);
}

.ai-visibility-delete-report-dialog__cancel:disabled,
.ai-visibility-delete-report-dialog__delete:disabled {
  cursor: default;
  opacity: 0.55;
}

</style>
